import { useState, type ReactElement, type ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';

import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import { Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import AppCustomIcon from '../features/icon/AppIcon';
import AppNavigationDrawer from '../features/navigation/features/drawer/AppNavigationDrawer';
import { AppLogoTextWhite } from '../features/logo/AppLogo';
import AppDarkModeSwitch from '../features/darkmode/switch/AppDarkModeSwitch';
import AuthRoutes from '../../apps/auth/AuthRoutes';
import HAppRoutes from '../features/routes/helpers/HAppRoutes';
import { useAppNavigationContext } from '../features/navigation/providers/AppNavigationProvider';
import AdminShareLink from '../../apps/admin/features/share-link/AdminShareLink';
import InvestorRoutes from '../../apps/investor/InvestorRoutes';
import { useAuthContext } from '../../apps/auth/contexts/AuthContext';
import AppNotification from '../features/notification/AppNotification';
import AppNotificationProvider from '../features/notification/providers/AppNotificationProvider';
import AdminRoutes from '../../apps/admin/AdminRoutes';
import AppErrorBoundary from '../features/error/boundary/AppErrorBoundary';
import FUserCompanySelect from '../../apps/user/features/FUserCompanySelect';
import IAppNavigation from '../features/navigation/interfaces/IAppNavigation';
import AppLangSelect from '../features/lang/features/select/AppLangSelect';

export const drawerWidth = 280;
type LAppOnlineProps = {
	window?: Window;
	navigationConfig?: IAppNavigation[];
	titleComponent?: ReactElement;
	bottomComponent?: ReactElement;
	children: ReactNode;
};
const LAppOnline = (props: LAppOnlineProps) => {
	const { window, navigationConfig, titleComponent, bottomComponent } = props;
	const authContext = useAuthContext();
	const appNavigationContext = useAppNavigationContext();
	const navigate = useNavigate();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [mobileOpen, setMobileOpen] = useState(false);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleDrawerClose = () => {
		setMobileOpen(false);
	};

	const handleLogout = () => {
		navigate(HAppRoutes.getPath(AuthRoutes.logout.tag));
	};

	const handleClickProfile = () => {
		navigate(HAppRoutes.getPath(InvestorRoutes.profile.tag));
	};

	const container = window !== undefined ? () => window.document.body : undefined;

	return (
		<AppNotificationProvider>
			<Box sx={{ display: 'flex' }}>
				<AppBar
					position="fixed"
					style={{
						backgroundImage: 'none',
						backgroundColor: theme.palette.background.default,
						opacity: 1,
						boxShadow: 'none',
					}}
					sx={{
						width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
						ml: isMobile ? '0px' : `${drawerWidth}px`,
					}}
				>
					<Toolbar>
						<IconButton
							color="primary"
							aria-label="open drawer"
							edge="start"
							onClick={handleDrawerToggle}
							sx={{ mr: 2, display: { sm: 'none' } }}
						>
							<AppCustomIcon tag="toggle_mobile" color="primary" />
						</IconButton>

						<Box
							sx={{
								flexGrow: 1,
							}}
						>
							<Stack direction="row" columnGap={2} alignItems="center">
								{titleComponent}
							</Stack>
						</Box>
						<Box sx={{ display: { xs: 'none', md: 'flex' } }}>
							<FUserCompanySelect />
							<AppDarkModeSwitch />
							{!authContext.authOverrideUserId && authContext.authContext === 'employee' ? (
								<AppLangSelect />
							) : (
								''
							)}
						</Box>

						<Box>
							{/* Common button for mobile and desktop */}
							{authContext.isInvestor ? <AppNotification /> : ''}
							{appNavigationContext.displayShareLink || authContext.isInvestor ? <AdminShareLink /> : ''}
							{authContext.isInvestor ? (
								<IconButton
									size="large"
									color="primary"
									id="icon-user-profile"
									onClick={() => handleClickProfile()}
								>
									<AppCustomIcon tag="user_account" color="primary" />
								</IconButton>
							) : (
								''
							)}
							<IconButton size="large" onClick={() => handleLogout()} id="btn-logout" color="primary">
								<AppCustomIcon tag="logout" color="primary" />
							</IconButton>
						</Box>
					</Toolbar>
				</AppBar>

				<Box component="nav" sx={{ width: isMobile ? '0px' : drawerWidth }}>
					<Drawer
						container={isMobile ? container : null}
						variant={isMobile ? 'temporary' : 'permanent'}
						open={mobileOpen}
						onClose={handleDrawerToggle}
						ModalProps={{
							keepMounted: true, // Better open performance on mobile.
						}}
						sx={{
							marginTop: '20px',
							position: 'relative',
							'& .MuiDrawer-paper': {
								boxSizing: 'border-box',
								width: isMobile ? '100%' : drawerWidth,
								padding: isMobile ? '0px' : '10px',
							},
						}}
					>
						{!isMobile ? (
							<Toolbar style={{ height: '100px' }}>
								<Link
									to={
										authContext.isInvestor
											? HAppRoutes.getPath(InvestorRoutes.dashboard.tag)
											: HAppRoutes.getPath(AdminRoutes.dashboard.tag)
									}
									style={{ margin: 'auto' }}
								>
									<AppLogoTextWhite style={{ height: '60px' }} />
								</Link>
							</Toolbar>
						) : (
							<Toolbar>
								<IconButton
									color="primary"
									aria-label="close drawer"
									edge="start"
									onClick={handleDrawerToggle}
									sx={{ mr: 2 }}
								>
									<AppCustomIcon tag="toggle_mobile_close" sx={{ color: 'white' }} />
								</IconButton>
								<AppLogoTextWhite style={{ height: '40px', marginTop: '15px', marginLeft: '-50px' }} />

								<Box sx={{ flexGrow: 1 }} />
								<Box sx={{}}>
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										sx={{ marginRight: '0px', marginLeft: '10px', marginTop: '-5px' }}
									>
										<AppDarkModeSwitch />
										{/* <AppLangSelect style={{ color: '#FFF' }} /> */}
									</Stack>
								</Box>
							</Toolbar>
						)}
						<Box
							sx={{
								padding: isMobile ? '10px' : '0px',
								height: isMobile ? 'calc(100% - 50px)' : 'calc(100% - 100px)',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Box>
								{isMobile ? <FUserCompanySelect /> : ''}
								<AppNavigationDrawer
									navigationConfig={navigationConfig}
									onClose={() => handleDrawerClose()}
								/>
							</Box>
							{bottomComponent ? <Box sx={{ marginTop: 'auto' }}>{bottomComponent}</Box> : ''}
						</Box>
					</Drawer>
				</Box>
				<Box
					component="main"
					sx={{
						flexGrow: 1,
						width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
						marginBottom: '24px',
					}}
				>
					<Toolbar />
					<Container maxWidth="xl">
						<AppErrorBoundary>{props.children}</AppErrorBoundary>
					</Container>
				</Box>
			</Box>
		</AppNotificationProvider>
	);
};

export default LAppOnline;
